import { useState } from 'react';
import { TriangleAlert, X } from 'lucide-react';
import { useActions, useSafety } from '@/store/selectors';
import { OverrideReasonModal } from '@/components/modals/OverrideReasonModal';

/** Safety banner: contraindications, EI gate, toxicity risk, implausible inputs (TODO Task 1.26). */
export function SafetyBanner(): JSX.Element | null {
  const safety = useSafety();
  const actions = useActions();
  const [overrideModal, setOverrideModal] = useState<string | null>(null);

  if (!safety) return null;

  const hasBlocking = safety.eiBlocked || safety.contraindications.some((c) => c.severity === 'block' && !c.overridden);
  const hasWarnings = safety.contraindications.some((c) => c.severity === 'warn');
  const hasImplausible = safety.implausibleInput.length > 0;

  if (!hasBlocking && !hasWarnings && !hasImplausible && safety.toxicityRisk === 'low') return null;

  const submitOverride = (reason: string): void => {
    if (overrideModal) {
      actions.overrideContraindication(overrideModal, reason);
    }
    setOverrideModal(null);
  };

  return (
    <section className={`safety-banner animate-enter-up ${hasBlocking ? 'safety-block' : 'safety-warn'}`} role={hasBlocking ? 'alert' : 'status'} aria-live={hasBlocking ? 'assertive' : 'polite'} data-testid="safety-banner">
      <h3 className="safety-title">
        <TriangleAlert size={18} strokeWidth={2} aria-hidden="true" />
        {hasBlocking ? 'Contraindication — EI Blocked' : 'Safety Warning'}
      </h3>

      {safety.eiBlocked && (
        <div className="safety-section">
          <strong>Extended-interval dosing is blocked:</strong>
          <ul>
            {safety.eiBlockReasons.map((r, i) => (
              <li key={`eiblock-${i}`}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {safety.contraindications.length > 0 && (
        <div className="safety-section">
          <strong>Contraindications:</strong>
          <ul>
            {safety.contraindications.map((c) => (
              <li key={c.type} className={`${c.overridden ? 'strikethrough' : ''} overflow-anywhere`}>
                <span className={`badge ${c.severity === 'block' ? 'badge-danger' : 'badge-warn'}`}>
                  {c.severity === 'block'
                    ? <X size={12} strokeWidth={2.5} aria-hidden="true" />
                    : <TriangleAlert size={12} strokeWidth={2} aria-hidden="true" />}
                  {c.type}
                </span>
                {c.overridden && <em> — overridden: {c.overrideReason}</em>}
                {!c.overridden && c.severity === 'block' && (
                  <button className="btn btn-secondary btn-sm mt-2" onClick={() => setOverrideModal(c.type)}>
                    Override
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasImplausible && (
        <div className="safety-section">
          <strong>Implausible inputs:</strong>
          <ul>
            {safety.implausibleInput.map((f, i) => (
              <li key={`imp-${f.field}-${i}`}>
                {f.field} = {f.value} ({f.reason}; expected {f.validRange[0]}–{f.validRange[1]})
              </li>
            ))}
          </ul>
        </div>
      )}

      {safety.toxicityRisk !== 'low' && (
        <div className="safety-section">
          <strong>Toxicity risk: {safety.toxicityRisk.toUpperCase()}</strong>
          {safety.toxicityRiskFactors.length > 0 && (
            <ul>
              {safety.toxicityRiskFactors.map((rf, i) => (
                <li key={`tox-${i}`}>{rf}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {safety.unstableRenalWarning && (
        <div className="badge badge-warn">
          <TriangleAlert size={12} strokeWidth={2} aria-hidden="true" />
          Renal function unstable — use SCr closest to dosing time; recheck within 24 h.
        </div>
      )}

      <OverrideReasonModal
        open={overrideModal !== null}
        title={overrideModal ? `Override: ${overrideModal}` : 'Document Override'}
        onSubmit={submitOverride}
        onCancel={() => setOverrideModal(null)}
      />
    </section>
  );
}
